import React from "react";
import Layout from "../Layout";
import { useTranslation } from "react-i18next";
import { Code, Search, Wallet, Palette, ArrowRight } from "lucide-react";
import imageService from "../../../assets/Selection.png";
import { motion } from "framer-motion";

function ServicesPage() {
  const { t } = useTranslation();

  const services = [
    {
      icon: <Code className="text-MainBlue w-8 h-8 md:w-10 md:h-10" />,
      title: t("services.webDevelopmentTitle"),
      desc: t("services.webDevelopmentDesc"),
    },
    {
      icon: <Search className="text-MainBlue w-8 h-8 md:w-10 md:h-10" />,
      title: t("services.seoTitle"),
      desc: t("services.seoDesc"),
    },
    {
      icon: <Wallet className="text-MainBlue w-8 h-8 md:w-10 md:h-10" />,
      title: t("services.paymentLinkTitle"),
      desc: t("services.paymentLinkDesc"),
    },
    {
      icon: <Palette className="text-MainBlue w-8 h-8 md:w-10 md:h-10" />,
      title: t("services.graphicDesignTitle"),
      desc: t("services.graphicDesignDesc"),
    },
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const ServiceItem = {
    hidden: { opacity: 0, y: 40 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.6 } 
    },
  };

  return (
    <Layout title={t("pages.services")}>
      {/* Hero Section */}
      <section className="py-8 md:py-12 lg:py-16 relative overflow-hidden">
        {/* خلفية متدرجة */}
        <div className="absolute inset-0 bg-gradient-to-br from-MainBlue/5 via-blue-600/3 to-MainBlue/5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-MainBlue/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-4 md:space-y-6"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-MainBlue via-blue-600 to-MainBlue bg-clip-text text-transparent">
                  {t("services.ourServices")}
                </span>
              </h1>
              <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed">
                {t("services.ourServicesDesc")}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-MainBlue/20 to-blue-600/20 rounded-2xl blur-3xl"></div>
              <motion.img
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
                src={imageService}
                alt="Our Services"
                className="relative z-10 rounded-xl md:rounded-2xl shadow-xl drop-shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-12 md:py-16 lg:py-20 bg-gradient-to-br from-muted via-muted/50 to-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 md:mb-14"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-MainBlue to-blue-600 bg-clip-text text-transparent">
                {t("services.coreServices")}
              </span>
            </h2>
          </motion.div>

          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
          >
            {services.map((s, i) => (
              <motion.div
                key={i}
                variants={ServiceItem}
                whileHover={{ y: -8, scale: 1.02 }}
                className="bg-card rounded-xl md:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 p-6 md:p-8 border border-border/50 flex items-start gap-4 md:gap-6 group cursor-pointer"
              >
                <div className="flex-shrink-0 p-3 md:p-4 bg-gradient-to-br from-MainBlue/10 to-blue-600/10 group-hover:from-MainBlue/20 group-hover:to-blue-600/20 rounded-xl transition-all duration-300">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    {s.icon}
                  </motion.div>
                </div>
                <div className="flex-1">
                  <h4 className="text-lg md:text-xl font-bold text-foreground mb-2 group-hover:text-MainBlue transition-colors">
                    {s.title}
                  </h4>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 lg:py-20 relative overflow-hidden">
        {/* خلفية متدرجة */}
        <div className="absolute inset-0 bg-gradient-to-br from-MainBlue/10 via-blue-600/5 to-MainBlue/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-MainBlue/5 rounded-full blur-3xl"></div>

        <div className="relative max-w-4xl mx-auto px-4 md:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6 md:space-y-8"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
              <span className="bg-gradient-to-r from-MainBlue via-blue-600 to-MainBlue bg-clip-text text-transparent">
                {t("features.ctaTitle") || "Ready to Get Started?"}
              </span>
            </h2>
            <motion.a
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              href="https://wa.me/971528401112?text=Hello%21+I+found+TWHMENU.com+and+I+want+to+create+digital+menu"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-MainBlue to-blue-600 hover:from-blue-600 hover:to-MainBlue text-white px-8 md:px-10 py-4 md:py-5 rounded-lg md:rounded-xl font-semibold text-base md:text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <span>{t("features.ctaButton") || "Contact Us"}</span>
              <motion.div
                initial={{ x: 0 }}
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <ArrowRight className="w-5 h-5" />
              </motion.div>
            </motion.a>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}

export default ServicesPage;
